import { useEffect } from 'react';
import { makeStyles, tokens } from '@fluentui/react-components';
import { useNavigate } from 'react-router-dom';
import { useDiscovery } from './hooks/useDiscovery';
import { AnalysisProgressView } from './components/AnalysisProgress/AnalysisProgress';
import { ConsentRequired } from './components/ConsentRequired/ConsentRequired';
import { ReportDownload } from './components/ReportDownload/ReportDownload';
import { PageBanner } from '@/components/common/PageBanner';

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalXXL,
  },
  breadcrumb: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    alignItems: 'center',
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  activeStep: {
    color: tokens.colorBrandForeground1,
    fontWeight: 600,
  },
});

const STEP_LABELS = {
  analysis: 'Analysing',
  download: 'Download Report',
};

export function DiscoveryPage() {
  const styles = useStyles();
  const navigate = useNavigate();
  const discovery = useDiscovery();

  // Redirect to home if no form data was provided
  useEffect(() => {
    if (!discovery.selectedIndustry || !discovery.userContext) {
      navigate('/', { replace: true });
    }
  }, [discovery.selectedIndustry, discovery.userContext, navigate]);

  if (!discovery.selectedIndustry || !discovery.userContext) {
    return null;
  }

  return (
    <div className={styles.page}>
      <PageBanner
        kicker="AI Readiness · Analysis"
        title="Generating Your Report"
        subtitle="Hold tight — we're building your personalised AI readiness report from the details you provided."
      />
      {/* Breadcrumb */}
      <div className={styles.breadcrumb}>
        {(['analysis', 'download'] as const).map((step, i) => (
          <span key={step}>
            {i > 0 && <span style={{ margin: '0 4px' }}>/</span>}
            <span
              className={
                step === discovery.wizardStep ? styles.activeStep : undefined
              }
            >
              {STEP_LABELS[step]}
            </span>
          </span>
        ))}
      </div>

      {/* Consent required screen (takes priority over analysis progress) */}
      {discovery.status === 'failed' && discovery.consentUrl && (
        <ConsentRequired
          consentUrl={discovery.consentUrl}
          onReset={discovery.reset}
        />
      )}

      {/* Wizard steps */}
      {discovery.wizardStep === 'analysis' &&
        !(discovery.status === 'failed' && discovery.consentUrl) && (
        <AnalysisProgressView
          currentStep={discovery.currentStep ?? null}
          stepsCompleted={discovery.stepsCompleted ?? []}
          progressPct={discovery.progressPct}
          message={discovery.message}
          error={discovery.error}
        />
      )}

      {discovery.wizardStep === 'download' &&
        discovery.reportReady &&
        discovery.fileName && (
          <ReportDownload fileName={discovery.fileName} />
        )}
    </div>
  );
}
